import { baseApi } from 'core/api';
import { useToastStore } from 'store/modules';

const state = () => ({
    _loading: false,
    _voicesLoading: false,
    _voicesBasic: null,
    _voicesPremium: null,
    _voicesCustom: null,
    _voice: null,
    _speechesPreview: null,
    _speeches: null,
    _speechesLoading: false,
    _statsLoading: false,
    _availableNumberCustomVoices: 0,
});

const getters = {
    voicesLoading: (state) => state._voicesLoading,
    previewLoading: (state) => state._loading,
    voicesBasic: (state) => state._voicesBasic,
    voicesPremium: (state) => state._voicesPremium,
    voicesCustom: (state) => state._voicesCustom,
    voiceType: (state) => state._voice,
    speechesPreview: (state) => state._speechesPreview,
    speeches: (state) => state._speeches,
    speechesLoading: (state) => state._speechesLoading,
    stats: (state) => state._statsLoading,
    availableNumberCustomVoices: (state) => state._availableNumberCustomVoices,
};

const actions = {
    setVoice({ data }) {
        this._setSmartTtsVoiceStateUpdate({
            voice: data,
        });
    },

    resetVoice() {
        this._setSmartTtsVoiceStateUpdate({
            voice: null,
        });
    },

    getVoiceSampleUrl({ type, id }) {
        return baseApi
            .get(`smart-tts/voices/${type}/${id}/url`)
            .then((response) => response.data.payload)
            .catch((err) => this.handleApiError(err));
    },

    getTypeList(type) {
        const typeCap = type[0].toUpperCase() + type.slice(1);

        this._setSmartTtsVoiceStateUpdate({
            voicesLoading: true,
        });

        return baseApi
            .get(`smart-tts/voices/${type}`)
            .then((response) => {
                this._setSmartTtsVoiceStateUpdate({
                    [`voices${typeCap}`]: response.data.payload,
                });
                return response.data.payload;
            })
            .catch((err) => this.handleApiError(err))
            .finally(() => {
                this._setSmartTtsVoiceStateUpdate({
                    voicesLoading: false,
                });
            });
    },

    createCustomVoice({ name, sample }) {
        const data = new FormData();

        data.append('name', name);
        data.append('sample', sample);

        return baseApi
            .post('smart-tts/voices/custom', data)
            .then((response) => {
                if (!(response.meta?.status?.code === 422)) {
                    this.getTypeList('custom');
                }
            })
            .catch((err) => this.handleApiError(err));
    },

    getSpeechesPreview({ id, text }) {
        this._setSmartTtsVoiceStateUpdate({
            loading: true,
        });

        const data = new FormData();

        data.append('voice_id', id);
        data.append('text', text);

        return baseApi
            .post('smart-tts/speeches/preview', data, {
                headers: {
                    Accept: 'audio/mpeg',
                },
                responseType: 'blob',
            })
            .then((response) => {
                this._setSmartTtsVoiceStateUpdate({
                    speechesPreview: response.data,
                });

                return response.data;
            })
            .catch((err) => this.handleApiError(err))
            .finally(() => {
                this._setSmartTtsVoiceStateUpdate({
                    loading: false,
                });
            });
    },

    getSpeeches({ id, text, group }) {
        this._setSmartTtsVoiceStateUpdate({
            speechesLoading: true,
        });

        const data = new FormData();

        data.append('voice_id', id);
        data.append('text', text);
        data.append('group', group);

        return baseApi
            .post('smart-tts/speeches', data)
            .then((response) => {
                this._setSmartTtsVoiceStateUpdate({
                    speeches: response.data,
                });

                return response.data;
            })
            .catch((err) => this.handleApiError(err))
            .finally(() => {
                this._setSmartTtsVoiceStateUpdate({
                    speechesLoading: false,
                });
            });
    },

    deleteCustomVoiceById({ voiceId }) {
        return baseApi
            .delete(`smart-tts/voices/custom/${voiceId}`)
            .then(() => {
                this._setSmartTtsCustomVoiceDelete(voiceId);
            })
            .catch((e) => e);
    },

    getVoicesStats() {
        this._setSmartTtsVoiceStateUpdate({
            statsLoading: true,
        });

        return baseApi
            .get('smart-tts/organization/stats')
            .then((response) => {
                this._setSmartTtsVoiceStateUpdate({
                    availableNumberCustomVoices: response?.data?.payload?.voice?.custom?.left,
                });
                return response.data.payload;
            })
            .catch((err) => this.handleApiError(err))
            .finally(() => {
                this._setSmartTtsVoiceStateUpdate({
                    statsLoading: false,
                });
            });
    },

    getSpeechesStream({ speechId }) {
        this._setSmartTtsVoiceStateUpdate({
            loading: true,
        });

        return baseApi
            .get(`smart-tts/speeches/${speechId}/stream`, {
                headers: {
                    Accept: 'audio/mpeg',
                },
                responseType: 'blob',
            })
            .then((response) => {
                this._setSmartTtsVoiceStateUpdate({
                    speechesPreview: response.data,
                });

                return response.data;
            })
            .catch((err) => this.handleApiError(err))
            .finally(() => {
                this._setSmartTtsVoiceStateUpdate({
                    loading: false,
                });
            });
    },

    handleApiError(error) {
        const errorMessage = error.response?.data?.meta?.error?.text;
        const message = errorMessage ?? 'Sorry, something went wrong';

        useToastStore().showToast({ type: 'danger', message });
    },

    // Private methods
    _setSmartTtsVoiceStateUpdate(data) {
        for (let key in data) {
            this[`_${key}`] = data[key];
        }
    },

    _setSmartTtsCustomVoiceDelete(id) {
        this._voicesCustom = this._voicesCustom.filter((voice) => voice.id !== id);
    },
};

export default {
    state,
    getters,
    actions,
};
